import { AdrDto } from "@cradua/data";
import { createReducer, on, Action } from "@ngrx/store";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";

import * as AdrActions from "./adr.actions";

export const ADRPROGRESS_FEATURE_KEY = "adr";

export interface AdrState extends EntityState<AdrDto> {
  selectedAdrId?: string | number; // which Adr record has been selected
  loaded: boolean; // has the Adr list been loaded
  error?: string | null; // last none error (if any)
}

export interface AdrPartialState {
  readonly [ADRPROGRESS_FEATURE_KEY]: AdrState;
}

export const adrAdapter: EntityAdapter<AdrDto> =
  createEntityAdapter<
    AdrDto
  >({
    selectId: (Adr) => Adr.id,
    sortComparer: sortByName,
  });

export function sortByName(a: AdrDto, b: AdrDto): number {
  return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
}

export const initialState: AdrState = adrAdapter
  .getInitialState(
    {
      // set initial required properties
      loaded: false,
      error: null,
      selectedAdrId: null,
    },
  );

const adrReducer = createReducer(
  initialState,
  on(AdrActions.loadAdr, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.loadAdrSuccess,
    (state, { Adr }) =>
      adrAdapter.setAll(Adr, {
        ...state,
        loaded: false,
      }),
  ),
  on(AdrActions.loadAdrFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(AdrActions.fetchAdrRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.fetchAdrRecordSuccess,
    (state, { record }) =>
      adrAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(AdrActions.fetchAdrRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AdrActions.fetchByIdAdrRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.fetchByIdAdrRecordSuccess,
    (state, { record }) =>
      adrAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(AdrActions.fetchByIdAdrRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AdrActions.addAdr, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.addAdrSuccess,
    (state, { newAdr }) =>
      adrAdapter.addOne(newAdr, {
        ...state,
        loaded: false,
      }),
  ),
  on(AdrActions.addAdrFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(AdrActions.updateAdr, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.updateAdrSuccess,
    (state, { updateAdr }) =>
      adrAdapter.upsertOne(updateAdr, {
        ...state,
        loaded: false,
      }),
  ),
  on(AdrActions.updateAdrFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  // search correspondence

  on(AdrActions.searchAdrRecord, (state, { query }) => {
    return {
      ...state,
      selectedAdrId: query,
      loaded: false,
      error: null,
    };
  }),
  on(AdrActions.searchAdrIdRecord, (state, { query }) => {
    return {
      ...state,
      selectedAdrId: query,
      loaded: false,
      error: null,
    };
  }),
  on(AdrActions.removeAdr, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.removeAdrSuccess,
    (state, { id }) =>
      adrAdapter.removeOne(id, { ...state, loaded: false }),
  ),
  on(AdrActions.removeAdrFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AdrActions.clearAdr, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AdrActions.clearAdrSuccess,
    (state, { Adr }) =>
      adrAdapter.setAll(Adr, {
        ...state,
        loaded: false,
      }),
  ),
);

export function reducer(state: AdrState | undefined, action: Action) {
  return adrReducer(state, action);
}